import {useState} from 'react';

import {
  useAllQueuesQuery,
  usePublicQueueLiteQuery,
  usePublicQueueQuery,
  useQueueByIdLiteQuery,
  useQueueByIdQuery,
  useQueueNamesQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {usePublicQueueAccess} from '../hooks';
import {
  WANDB_ENTITY_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from '../publicQueue/constants';
import {PageInfo} from '../types';
import {
  extractQueue,
  useExtractPublicQueues,
  useExtractQueues,
} from './queueUtil';

export const useQueueNames = (entityName: string) => {
  const query = useQueueNamesQuery({variables: {entityName}});

  type LiteQueue = {
    name: string;
    id: string;
  };
  const queues: LiteQueue[] = query.data ? query.data.project!.runQueues : [];

  return {
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
    queues,
  };
};

export const usePublicQueues = (skipQuery?: boolean) => {
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const queryPublicQueue = usePublicQueueQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      publicQueueName: WANDB_PUBLIC_QUEUE_NAME,
    },
    pollInterval: 30000,
    skip: !hasPublicQueueAccess || isFeatureFlagLoading || skipQuery,
    context: propagateErrorsContext(),
  });

  const publicQueues = useExtractPublicQueues(queryPublicQueue);

  return {
    loading: queryPublicQueue.loading || isFeatureFlagLoading,
    error: queryPublicQueue.error,
    refetch: queryPublicQueue.refetch,
    queues: publicQueues,
  };
};

export const usePublicQueuesLite = (skipQuery?: boolean) => {
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const shouldSkip = !hasPublicQueueAccess || isFeatureFlagLoading || skipQuery;

  const queryPublicQueue = usePublicQueueLiteQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      publicQueueName: WANDB_PUBLIC_QUEUE_NAME,
    },
    pollInterval: shouldSkip ? undefined : 30000,
    skip: shouldSkip,
    context: propagateErrorsContext(),
  });

  const publicQueues = useExtractPublicQueues(queryPublicQueue);

  return {
    loading: queryPublicQueue.loading || isFeatureFlagLoading,
    error: queryPublicQueue.error,
    refetch: queryPublicQueue.refetch,
    queues: publicQueues,
  };
};

/**
 * Return information about the queues in all entities.
 */
export const useQueues = () => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';
  const entity = viewer?.entity;

  const queryAll = useAllQueuesQuery({
    pollInterval: 30000,
  });

  const skipPublicQueueQuery = entity === WANDB_ENTITY_NAME;
  const {
    queues: publicQueues,
    loading: publicQueuesLoading,
    error: publicQueuesError,
  } = usePublicQueuesLite(skipPublicQueueQuery);

  const queues = useExtractQueues(queryAll, personalEntity);
  const allQueues = publicQueues ? [...publicQueues, ...queues] : queues;

  return {
    loading: queryAll.loading || publicQueuesLoading,
    error: queryAll.error || publicQueuesError,
    refetch: queryAll.refetch,
    queues: allQueues,
  };
};

export const useQueue = (
  entityName: string,
  queueId?: string,
  first: number = 50,
  after?: string
) => {
  const [allItems, setAllItems] = useState<any[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>({});

  const {data, error, loading, refetch, fetchMore} = useQueueByIdQuery({
    variables: {entityName, queueId: queueId ?? '', first, after},
    skip: !queueId,
    context: propagateErrorsContext(),
    onCompleted: data => {
      const items = data?.project?.runQueue?.runQueueItems?.edges || [];
      if (items.length > 0 && allItems.length === 0) {
        setAllItems(items);
        setPageInfo({
          hasNextPage:
            data?.project?.runQueue?.runQueueItems?.pageInfo?.hasNextPage,
          endCursor:
            data?.project?.runQueue?.runQueueItems?.pageInfo?.endCursor ??
            undefined,
        });
      }
    },
  });

  if (loading || error) {
    return {error, loading, queue: undefined, refetch, fetchMore};
  }
  if (!data?.project?.runQueue) {
    // TODO: What is the correct way to specify error? GraphQLError?
    return {
      error: `No queue with id ${queueId} in entity ${entityName}`,
      loading: undefined,
      queue: undefined,
      fetchMore: undefined,
    };
  }

  const dataWithMergedItems = {
    ...data,
    project: {
      ...data.project,
      runQueue: {
        ...data.project.runQueue,
        runQueueItems: {
          ...data.project.runQueue.runQueueItems,
          edges:
            allItems.length > 0
              ? allItems
              : data.project.runQueue.runQueueItems?.edges,
          pageInfo:
            allItems.length > 0
              ? pageInfo
              : data.project.runQueue.runQueueItems?.pageInfo,
        },
      },
    },
  };

  const queue = extractQueue(entityName, dataWithMergedItems as any);

  return {
    queue,
    loading: undefined,
    error: undefined,
    refetch,
    fetchMore,
    hasNextPage: pageInfo.hasNextPage,
    endCursor: pageInfo.endCursor,
    setAllItems,
    setPageInfo,
  };
};

export const useQueueLite = (entityName: string, queueId?: string) => {
  const shouldSkip = !queueId;
  const {data, error, loading, refetch} = useQueueByIdLiteQuery({
    variables: {entityName, queueId: queueId ?? ''},
    pollInterval: shouldSkip ? undefined : 5000,
    skip: shouldSkip,
    context: propagateErrorsContext(),
  });

  if (loading || error) {
    return {error, loading, queue: undefined, refetch};
  }
  if (!data?.project?.runQueue) {
    // Queue doesn't exist or data not ready yet
    return {
      error: undefined,
      loading: undefined,
      queue: undefined,
      refetch,
    };
  }
  const queue = extractQueue(entityName, data);

  return {
    queue,
    loading: undefined,
    error: undefined,
    refetch,
  };
};
